import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Crown, 
  Check, 
  Clock,
  AlertTriangle,
  Sparkles,
  CreditCard,
  Upload,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Loader2,
  Copy,
  Phone,
  Wallet
} from 'lucide-react';

// Step indicators
const STEPS = {
  SELECT_PLAN: 1,
  SELECT_METHOD: 2,
  PAYMENT_DETAILS: 3,
  UPLOAD_PROOF: 4,
  STATUS: 5
};

export default function SubscriptionPage() {
  const { user, subscriptionStatus, checkAuth } = useAuth();
  const navigate = useNavigate();
  
  // Multi-step state
  const [currentStep, setCurrentStep] = useState(STEPS.SELECT_PLAN);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [existingOrder, setExistingOrder] = useState(null);
  
  // Order state
  const [selectedPlan, setSelectedPlan] = useState(null); // 'first_year' or 'renewal'
  const [paymentMethod, setPaymentMethod] = useState(null); // 'gcash' or 'maya'
  const [contactNumber, setContactNumber] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Extension request state
  const [requestingExtension, setRequestingExtension] = useState(false);
  
  // Countdown timer state
  const [minutesRemaining, setMinutesRemaining] = useState(null);

  useEffect(() => {
    fetchSettings();
    checkExistingOrder();
  }, []);

  // Countdown timer effect
  useEffect(() => {
    let interval;
    if (currentStep === STEPS.UPLOAD_PROOF && orderDetails && !orderDetails.proof_of_payment_url) {
      // Calculate initial time remaining
      const createdAt = new Date(orderDetails.created_at);
      const expiresAt = new Date(createdAt.getTime() + 2 * 60 * 60 * 1000); // 2 hours
      
      const updateTimer = () => {
        const now = new Date();
        const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000 / 60));
        setMinutesRemaining(remaining);
        
        if (remaining <= 0) {
          // Order expired, refresh to show updated status
          toast.error('Order expired! Please create a new payment order.');
          setCurrentStep(STEPS.SELECT_PLAN);
          setOrderId(null);
          setOrderDetails(null);
          setExistingOrder(null);
          clearInterval(interval);
        }
      };
      
      updateTimer();
      interval = setInterval(updateTimer, 60000); // Update every minute
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentStep, orderDetails]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/subscription/settings');
      setSettings(response.data);
    } catch (error) {
      // Use defaults if settings not available
      setSettings({
        first_year_price: 699.00,
        renewal_price: 99.00,
        include_vat: true,
        vat_percentage: 12.0,
        gcash_number: '09164543040',
        maya_number: '09164543040',
        account_name: 'Severino Guillermo'
      });
    }
  };

  const checkExistingOrder = async () => {
    try {
      const response = await api.get('/subscription/my-orders');
      const orders = response.data.orders || [];
      const pendingOrder = orders.find(o => o.status === 'pending');
      
      if (pendingOrder) {
        setExistingOrder(pendingOrder);
        setOrderId(pendingOrder.order_id);
        setOrderDetails(pendingOrder);
        setPaymentMethod(pendingOrder.payment_method);
        
        // If proof already uploaded, go to status
        if (pendingOrder.proof_of_payment_url) {
          setCurrentStep(STEPS.STATUS);
        } else {
          // Go to upload proof step
          setCurrentStep(STEPS.UPLOAD_PROOF);
        }
      }
    } catch (error) {
      console.log('No existing orders');
    }
  };

  const calculatePrice = (planType) => {
    if (!settings) return { base: 0, vat: 0, total: 0 };
    
    const basePrice = planType === 'first_year' 
      ? settings.first_year_price 
      : settings.renewal_price;
    
    const vatAmount = settings.include_vat 
      ? basePrice * (settings.vat_percentage / 100) 
      : 0;
    
    return {
      base: basePrice,
      vat: vatAmount,
      total: basePrice + vatAmount
    };
  };

  const handleRequestExtension = async () => {
    try {
      setRequestingExtension(true);
      await api.post('/auth/request-extension');
      toast.success('Extension request submitted! Admin will review within 24 hours.');
      await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request extension');
    } finally {
      setRequestingExtension(false);
    }
  };

  const canRequestExtension = () => {
    if (!subscriptionStatus) return false;
    if (subscriptionStatus.subscription_status !== 'trial') return false;
    if (subscriptionStatus.extension_requested) return false;
    return true;
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setCurrentStep(STEPS.SELECT_METHOD);
  };

  const handleSelectMethod = (method) => {
    setPaymentMethod(method);
    setCurrentStep(STEPS.PAYMENT_DETAILS);
  };

  const handleCreateOrder = async () => {
    if (!selectedPlan || !paymentMethod) {
      toast.error('Please select a plan and payment method');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/subscription/create-order', {
        subscription_type: selectedPlan,
        payment_method: paymentMethod,
        contact_number: contactNumber
      });
      
      setOrderId(response.data.order_id);
      setOrderDetails(response.data);
      setCurrentStep(STEPS.UPLOAD_PROOF);
      toast.success('Order created! Please complete payment and upload proof.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image (JPEG, PNG, GIF, WEBP) or PDF file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);

      // Upload to backend
      const uploadResponse = await api.post('/upload/payment-proof', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      const fileUrl = uploadResponse.data.url;
      setUploadedFile({ name: file.name, url: fileUrl });

      // Update order with proof
      await api.put(`/subscription/upload-proof/${orderId}`, {
        proof_of_payment_url: fileUrl,
        proof_of_payment_filename: file.name
      });

      toast.success('Proof of payment uploaded successfully!');
      setCurrentStep(STEPS.STATUS);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8 overflow-x-auto">
      <div className="flex items-center gap-2 sm:gap-4 min-w-max px-4">
        {[
          { step: STEPS.SELECT_PLAN, label: 'Plan' },
          { step: STEPS.SELECT_METHOD, label: 'Method' },
          { step: STEPS.PAYMENT_DETAILS, label: 'Details' },
          { step: STEPS.UPLOAD_PROOF, label: 'Upload' },
          { step: STEPS.STATUS, label: 'Status' }
        ].map(({ step, label }, index) => (
          <React.Fragment key={step}>
            <div className={`flex flex-col items-center ${currentStep >= step ? 'text-emerald-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium 
                ${currentStep > step ? 'bg-emerald-600 text-white' : 
                  currentStep === step ? 'bg-emerald-100 text-emerald-600 border-2 border-emerald-600' : 
                  'bg-gray-100 text-gray-400'}`}>
                {currentStep > step ? <Check className="w-4 h-4" /> : step}
              </div>
              <span className="text-xs mt-1 hidden sm:block">{label}</span>
            </div>
            {index < 4 && (
              <div className={`w-8 sm:w-12 h-0.5 ${currentStep > step ? 'bg-emerald-600' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderPlanSelection = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Choose Your Plan</h2>
        <p className="text-gray-600 dark:text-gray-400">Select the subscription that fits your needs</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        {/* First Year Plan */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg border-2 
            ${selectedPlan === 'first_year' ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-gray-200 hover:border-emerald-300'}`}
          onClick={() => handleSelectPlan('first_year')}
          data-testid="plan-first-year"
        >
          <CardHeader className="text-center pb-2">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-full mx-auto mb-2">
              <Sparkles className="w-6 h-6 text-emerald-600" />
            </div>
            <CardTitle className="text-lg">First Year Subscription</CardTitle>
            <CardDescription>Full access for new subscribers</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <div className="text-4xl font-bold text-emerald-600 mb-1">
              ₱{settings?.first_year_price?.toFixed(2) || '699.00'}
            </div>
            {settings?.include_vat && (
              <p className="text-sm text-gray-500 mb-4">
                + {settings.vat_percentage}% VAT (₱{calculatePrice('first_year').vat.toFixed(2)})
              </p>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Total: <strong>₱{calculatePrice('first_year').total.toFixed(2)}</strong>
            </p>
            <ul className="text-sm text-left space-y-2">
              {['Full access to all laws & IRRs', 'AI-powered research assistant', 'Smart tools & calculators', '1 year validity'].map((feature, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Renewal Plan */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg border-2 
            ${selectedPlan === 'renewal' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'}`}
          onClick={() => handleSelectPlan('renewal')}
          data-testid="plan-renewal"
        >
          <CardHeader className="text-center pb-2">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mx-auto mb-2">
              <Crown className="w-6 h-6 text-blue-600" />
            </div>
            <CardTitle className="text-lg">Renewal</CardTitle>
            <CardDescription>For existing subscribers</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <div className="text-4xl font-bold text-blue-600 mb-1">
              ₱{settings?.renewal_price?.toFixed(2) || '99.00'}
            </div>
            {settings?.include_vat && (
              <p className="text-sm text-gray-500 mb-4">
                + {settings.vat_percentage}% VAT (₱{calculatePrice('renewal').vat.toFixed(2)})
              </p>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Total: <strong>₱{calculatePrice('renewal').total.toFixed(2)}</strong>
            </p>
            <ul className="text-sm text-left space-y-2">
              {['Continue full access', 'Keep all your bookmarks', 'Latest updates & features', '1 year extension'].map((feature, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderMethodSelection = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Select Payment Method</h2>
        <p className="text-gray-600 dark:text-gray-400">Choose how you'd like to pay</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
        {/* GCash */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg border-2 
            ${paymentMethod === 'gcash' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'}`}
          onClick={() => handleSelectMethod('gcash')}
          data-testid="method-gcash"
        >
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-blue-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Wallet className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">GCash</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Pay using your GCash wallet</p>
          </CardContent>
        </Card>

        {/* Maya */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg border-2 
            ${paymentMethod === 'maya' ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200 hover:border-green-300'}`}
          onClick={() => handleSelectMethod('maya')}
          data-testid="method-maya"
        >
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <CreditCard className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Maya</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Pay using your Maya wallet</p>
          </CardContent>
        </Card>
      </div>

      <div className="text-center mt-6">
        <Button variant="ghost" onClick={() => setCurrentStep(STEPS.SELECT_PLAN)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Plans
        </Button>
      </div>
    </div>
  );

  const renderPaymentDetails = () => {
    const prices = calculatePrice(selectedPlan);
    const accountNumber = paymentMethod === 'gcash' ? settings?.gcash_number : settings?.maya_number;

    return (
      <div className="space-y-6 max-w-xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Payment Details</h2>
          <p className="text-gray-600 dark:text-gray-400">Send payment to the account below</p>
        </div>

        {/* Payment Summary Card */}
        <Card className="border-2 border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20">
          <CardContent className="p-6">
            <h3 className="font-semibold text-lg mb-4 text-emerald-800 dark:text-emerald-200">Payment Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Plan:</span>
                <span className="font-medium">{selectedPlan === 'first_year' ? 'First Year' : 'Renewal'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Base Price:</span>
                <span>₱{prices.base.toFixed(2)}</span>
              </div>
              {settings?.include_vat && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">VAT ({settings.vat_percentage}%):</span>
                  <span>₱{prices.vat.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between text-lg font-bold text-emerald-700 dark:text-emerald-300">
                  <span>Total Amount:</span>
                  <span>₱{prices.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Details Card */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              {paymentMethod === 'gcash' ? (
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-white" />
                </div>
              ) : (
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-white" />
                </div>
              )}
              {paymentMethod === 'gcash' ? 'GCash' : 'Maya'} Account
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-gray-500 text-sm">Account Name</Label>
                <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mt-1">
                  <span className="font-medium">{settings?.account_name || 'Severino Guillermo'}</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(settings?.account_name || 'Severino Guillermo')}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-500 text-sm">Account Number</Label>
                <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mt-1">
                  <span className="font-medium text-lg tracking-wider">{accountNumber}</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(accountNumber)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-gray-500 text-sm">Amount to Send</Label>
                <div className="flex items-center justify-between bg-emerald-50 dark:bg-emerald-900/30 rounded-lg p-3 mt-1 border border-emerald-200">
                  <span className="font-bold text-xl text-emerald-700 dark:text-emerald-300">₱{prices.total.toFixed(2)}</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(prices.total.toFixed(2))}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Number (Optional) */}
        <div>
          <Label htmlFor="contact" className="text-gray-600">Your Contact Number (Optional)</Label>
          <div className="flex items-center gap-2 mt-1">
            <Phone className="w-4 h-4 text-gray-400" />
            <Input
              id="contact"
              type="tel"
              placeholder="09XX XXX XXXX"
              value={contactNumber}
              onChange={(e) => setContactNumber(e.target.value)}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">For payment verification purposes</p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button variant="outline" onClick={() => setCurrentStep(STEPS.SELECT_METHOD)} className="flex-1">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button 
            onClick={handleCreateOrder} 
            disabled={loading}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            data-testid="create-order-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Order...
              </>
            ) : (
              <>
                I've Sent Payment <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  const renderUploadProof = () => (
    <div className="space-y-6 max-w-xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Upload Proof of Payment</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Upload a screenshot or photo of your payment confirmation
        </p>
      </div>

      {/* Countdown Timer Warning */}
      {minutesRemaining !== null && minutesRemaining <= 120 && !uploadedFile && (
        <div className={`rounded-lg p-4 text-center ${
          minutesRemaining <= 30 
            ? 'bg-red-50 dark:bg-red-900/20 border border-red-200' 
            : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200'
        }`}>
          <div className="flex items-center justify-center gap-2">
            <Clock className={`w-5 h-5 ${minutesRemaining <= 30 ? 'text-red-600' : 'text-amber-600'}`} />
            <span className={`font-bold text-lg ${minutesRemaining <= 30 ? 'text-red-700' : 'text-amber-700'}`}>
              {minutesRemaining > 60 
                ? `${Math.floor(minutesRemaining / 60)}h ${minutesRemaining % 60}m remaining`
                : `${minutesRemaining} minutes remaining`
              }
            </span>
          </div>
          <p className={`text-sm mt-1 ${minutesRemaining <= 30 ? 'text-red-600' : 'text-amber-600'}`}>
            {minutesRemaining <= 30 
              ? 'Hurry! Your order will be automatically cancelled if proof is not uploaded soon.'
              : 'Please upload proof of payment within 2 hours or your order will be cancelled.'
            }
          </p>
        </div>
      )}

      {/* Order Info */}
      {orderDetails && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-800 dark:text-amber-200">Order ID</p>
                <p className="font-mono font-bold text-lg">#{orderId}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-amber-800 dark:text-amber-200">Amount</p>
                <p className="font-bold text-lg text-emerald-600">
                  ₱{orderDetails.total_amount?.toFixed(2) || calculatePrice(selectedPlan).total.toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Area */}
      <Card className="border-2 border-dashed border-gray-300 hover:border-emerald-400 transition-colors">
        <CardContent className="p-8">
          <div className="text-center">
            {uploading ? (
              <div className="space-y-4">
                <Loader2 className="w-12 h-12 mx-auto text-emerald-600 animate-spin" />
                <div>
                  <p className="font-medium">Uploading...</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-emerald-600 h-2 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{uploadProgress}%</p>
                </div>
              </div>
            ) : uploadedFile ? (
              <div className="space-y-4">
                <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
                <div>
                  <p className="font-medium text-emerald-600">File Uploaded!</p>
                  <p className="text-sm text-gray-500">{uploadedFile.name}</p>
                </div>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Drop your file here or click to browse
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Accepted: JPEG, PNG, GIF, WEBP, PDF (max 10MB)
                </p>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="proof-upload"
                  data-testid="proof-upload-input"
                />
                <Button asChild variant="outline" className="cursor-pointer">
                  <label htmlFor="proof-upload">
                    <Upload className="w-4 h-4 mr-2" /> Select File
                  </label>
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-sm">
        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Tips for a smooth verification:</h4>
        <ul className="text-blue-700 dark:text-blue-300 space-y-1">
          <li>• Make sure the screenshot shows the transaction reference number</li>
          <li>• The amount should be clearly visible</li>
          <li>• Date and time of transaction should be shown</li>
        </ul>
      </div>
    </div>
  );

  const renderStatus = () => {
    const order = existingOrder || orderDetails;
    const status = order?.status || 'pending';

    return (
      <div className="space-y-6 max-w-xl mx-auto text-center">
        {status === 'pending' && (
          <>
            <div className="w-20 h-20 bg-amber-100 rounded-full mx-auto flex items-center justify-center">
              <Clock className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Payment Under Review</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Your payment proof has been submitted and is being verified. 
              We'll notify you via email and in-app notification once it's approved.
            </p>
            <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200">
              <CardContent className="p-4">
                <p className="text-amber-800 dark:text-amber-200">
                  <strong>Order #{order?.order_id}</strong>
                  <br />
                  Expected verification time: Within 24 hours
                </p>
              </CardContent>
            </Card>
          </>
        )}

        {status === 'approved' && (
          <>
            <div className="w-20 h-20 bg-emerald-100 rounded-full mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-emerald-600">Payment Approved!</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Your subscription is now active. Enjoy full access to LexProcure PH!
            </p>
            <Button onClick={() => navigate('/library')} className="bg-emerald-600 hover:bg-emerald-700">
              Go to Library
            </Button>
          </>
        )}

        {status === 'rejected' && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full mx-auto flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-600">Payment Not Verified</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Unfortunately, we couldn't verify your payment.
            </p>
            {order?.rejection_reason && (
              <Card className="bg-red-50 dark:bg-red-900/20 border-red-200">
                <CardContent className="p-4">
                  <p className="text-red-800 dark:text-red-200">
                    <strong>Reason:</strong> {order.rejection_reason}
                  </p>
                </CardContent>
              </Card>
            )}
            <Button onClick={() => {
              setExistingOrder(null);
              setOrderId(null);
              setOrderDetails(null);
              setCurrentStep(STEPS.SELECT_PLAN);
            }} variant="outline">
              Try Again
            </Button>
          </>
        )}

        <div className="pt-6">
          <Button variant="ghost" onClick={() => navigate('/library')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Library
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-12">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-full mb-4 shadow-lg">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Subscribe to LexProcure PH
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Get unlimited access to Philippine procurement law resources
          </p>
        </div>

        {/* Trial Expired Alert */}
        {!subscriptionStatus?.is_trial_active && subscriptionStatus?.subscription_status === 'trial' && (
          <div className="max-w-xl mx-auto mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-amber-800 dark:text-amber-200 font-medium">Your trial has expired</p>
                {canRequestExtension() && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRequestExtension}
                    disabled={requestingExtension}
                    className="mt-2"
                  >
                    {requestingExtension ? 'Requesting...' : 'Request 5-day Extension'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step Indicator */}
        {currentStep !== STEPS.STATUS && renderStepIndicator()}

        {/* Main Content Card */}
        <Card className="max-w-4xl mx-auto shadow-xl">
          <CardContent className="p-6 sm:p-8">
            {currentStep === STEPS.SELECT_PLAN && renderPlanSelection()}
            {currentStep === STEPS.SELECT_METHOD && renderMethodSelection()}
            {currentStep === STEPS.PAYMENT_DETAILS && renderPaymentDetails()}
            {currentStep === STEPS.UPLOAD_PROOF && renderUploadProof()}
            {currentStep === STEPS.STATUS && renderStatus()}
          </CardContent>
        </Card>

        {/* Back to home link */}
        {currentStep === STEPS.SELECT_PLAN && (
          <div className="text-center mt-6">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
